<script setup>
import { useApiRequest } from '@/composables/useApiRequest'
import { useAuthStore } from '@/stores/auth'
import { emailValidator, requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const { loading, errors, request } = useApiRequest()

const form = ref({
  email: '',
  password: '',
  remember: false,
})

const formRef = ref()
const isPasswordVisible = ref(false)
const currentSlide = ref(0)

//  Slider Promo
// QUALIVA Phase 2 (lihat docs/cleanup/PHASE-1B-COMPLETION.md): teks slide
// diganti dari copy marketing POS lama (coffee shop/UMKM). Gambar
// /images/banner_*.jpg masih aset lama — perlu diganti aset QUALIVA sendiri.
const promoSlides = [
  {
    id: 1,
    title: 'Kelola Mutu Fasilitas Kesehatan',
    subtitle: 'Dokumen, checklist, audit, dan indikator mutu dalam satu sistem',
    image: '/images/banner_1.jpg',
  },
  {
    id: 2,
    title: 'Akreditasi Jadi Hasil Alami',
    subtitle: 'Bukan pekerjaan tambahan menjelang survei — dari aktivitas harian yang sudah terdokumentasi',
    image: '/images/banner_2.jpg',
  },
  {
    id: 3,
    title: 'Satu Sistem untuk Semua Proses Mutu',
    subtitle: 'Dari dokumen, workflow persetujuan, hingga penanganan insiden',
    image: '/images/banner_3.jpg',
  },
  {
    id: 4,
    title: 'Dirancang untuk Klinik & Praktik Mandiri',
    subtitle: 'Klinik Pratama, Klinik Utama, Praktik Dokter, dan Praktik Dokter Gigi',
    image: '/images/banner_4.jpg',
  },
]

// ✅ FIX: Fungsi login dengan error handling yang lebih baik
const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  try {
    const res = await request(() =>
      authStore.login({
        email: form.value.email,
        password: form.value.password,
      }),
    )

    if (res) {
      const redirectTo = route.query.to ? String(route.query.to) : '/dashboards/analytics'

      await router.push(redirectTo)
    }
  } catch (error) {
    console.error('Login error:', error)

    // Error sudah di-handle oleh useApiRequest dan akan muncul di errors.value
  }
}

// ✅ FIX: Error handling yang lebih robust
const generalError = computed(() => {
  // Cek error umum (_general)
  if (errors.value?._general?.[0]) {
    return errors.value._general[0]
  }
  
  // Cek error dari response (message)
  if (errors.value?.message) {
    return errors.value.message
  }
  
  return ''
})

// Auto-advance slider
const { pause, resume } = useIntervalFn(() => {
  currentSlide.value = (currentSlide.value + 1) % promoSlides.length
}, 6000)

onMounted(() => {
  resume()
})

onUnmounted(() => {
  pause()
})
</script>

<template>
  <div class="auth-wrapper min-h-screen d-flex">
    <VRow
      no-gutters
      class="flex-fill"
    >
      <!--  KOLOM KIRI: Hero Slider (75%) -->
      <VCol
        cols="12"
        md="9"
        class="d-none d-md-flex position-relative overflow-hidden"
      >
        <!-- Slider Background -->
        <VCarousel
          v-model="currentSlide"
          class="fill-height hero-carousel"
          hide-delimiters
          :show-arrows="false"
          :continuous="false"
          :touch="false"
          :cycle="false"
          :transition="false"
        >
          <VCarouselItem
            v-for="(slide, index) in promoSlides"
            :key="slide.id"
            class="fill-height"
          >
            <div class="hero-slide fill-height position-relative">
              <!-- Gambar -->
              <VImg
                :src="slide.image"
                class="fill-height"
                cover
                style="object-position: center center;"
              >
                <!-- Overlay gradient -->
                <div class="hero-overlay" />
                
                <!-- ✅ KONTEN DI KIRI ATAS -->
                <div class="hero-content-left">
                  <h2 class="hero-title">
                    {{ slide.title }}
                  </h2>
                  <p class="hero-subtitle">
                    {{ slide.subtitle }}
                  </p>
                </div>
              </VImg>

              <!-- Floating Cards di KANAN (berjajar vertikal) -->
              <div class="floating-cards-right">
                <!-- Card 1: Total Orders -->
                <VCard
                  class="floating-card"
                  rounded="lg"
                  elevation="0"
                >
                  <VCardText class="pa-4">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VAvatar
                        color="primary"
                        variant="tonal"
                        size="36"
                      >
                        <VIcon icon="ri-shopping-cart-2-line" />
                      </VAvatar>
                      <VChip
                        color="success"
                        size="small"
                        variant="flat"
                      >
                        +22%
                      </VChip>
                    </div>
                    <div class="text-h5 font-weight-bold">
                      155k
                    </div>
                    <div class="text-body-2 text-medium-emphasis">
                      Total Orders
                    </div>
                  </VCardText>
                </VCard>

                <!-- Card 2: New Customers -->
                <VCard
                  class="floating-card"
                  rounded="lg"
                  elevation="0"
                >
                  <VCardText class="pa-4">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VAvatar
                        color="success"
                        variant="tonal"
                        size="36"
                      >
                        <VIcon icon="ri-user-star-line" />
                      </VAvatar>
                      <VChip
                        color="error"
                        size="small"
                        variant="flat"
                      >
                        ↓ 8.1%
                      </VChip>
                    </div>
                    <div class="text-h5 font-weight-bold">
                      2,856
                    </div>
                    <div class="text-body-2 text-medium-emphasis">
                      New Customers
                    </div>
                  </VCardText>
                </VCard>

                <!-- Card 3: Revenue -->
                <VCard
                  class="floating-card"
                  rounded="lg"
                  elevation="0"
                >
                  <VCardText class="pa-4">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VAvatar
                        color="warning"
                        variant="tonal"
                        size="36"
                      >
                        <VIcon icon="ri-money-dollar-circle-line" />
                      </VAvatar>
                      <VChip
                        color="success"
                        size="small"
                        variant="flat"
                      >
                        +62%
                      </VChip>
                    </div>
                    <div class="text-h5 font-weight-bold">
                      $38.5k
                    </div>
                    <div class="text-body-2 text-medium-emphasis">
                      Sessions
                    </div>
                  </VCardText>
                </VCard>
              </div>

              <div class="bottom-text-container">
                <div class="bottom-text-content">
                  <VIcon
                    icon="ri-information-line"
                    class="me-2"
                  />
                  <span>QUALIVA siap digunakan — Kelola proses mutu fasilitas kesehatan Anda dengan lebih efisien</span>
                </div>
              </div>
            </div>
          </VCarouselItem>
        </VCarousel>

        <!-- Slider Indicators (dots) -->
        <div class="slider-indicators">
          <div
            v-for="(slide, index) in promoSlides"
            :key="slide.id"
            class="indicator-dot"
            :class="{ active: currentSlide === index }"
            @click="currentSlide = index"
          />
        </div>
      </VCol>

      <!-- 👉 KOLOM KANAN: Form Login (25%) -->
      <VCol
        cols="12"
        md="3"
        class="d-flex align-center justify-center pa-8 pa-md-12"
        style="background-color: rgb(var(--v-theme-surface));"
      >
        <div style="max-width: 380px; width: 100%;">
          <!-- Logo & Branding -->
          <!--
            QUALIVA Phase 2: placeholder wordmark — public/images/logo.png
            masih aset logo produk lama, belum ada aset brand QUALIVA sendiri
            (lihat docs/cleanup/PHASE-1B-COMPLETION.md). Ganti <h2> di bawah
            dengan <VImg src="..."> ke logo QUALIVA begitu asetnya tersedia. 
          -->
          <div class="text-center mb-8">
            <div class="logo-container mb-4">
              <VImg
                src="/images/logo.png"
                alt="PintuKasir Logo"
                max-width="180"
                max-height="80"
                contain
                class="mx-auto"
              />
            </div>
            <p class="text-body-1 text-medium-emphasis">
              Digital Quality Management System
            </p>
          </div>

          <!-- Welcome Text -->
          <div class="mb-6">
            <h4 class="text-h5 font-weight-bold mb-2">
              Selamat Datang! 👋🏻
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Silakan masuk ke akun Anda untuk melanjutkan
            </p>
          </div>

          <!-- ✅ ERROR ALERT (DIPERBAIKI) -->
          <VAlert
            v-if="generalError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
            closable
            @click:close="errors = {}"
          >
            {{ generalError }}
          </VAlert>

          <!-- Login Form -->
          <VForm
            ref="formRef"
            @submit.prevent="submit"
          >
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="form.email"
                  label="Email"
                  type="email"
                  placeholder="owner@kopisenja.com"
                  prepend-inner-icon="ri-mail-line"
                  :rules="[requiredValidator, emailValidator]"
                  :error-messages="errors.email"
                  variant="outlined"
                  density="comfortable"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="form.password"
                  label="Password"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  placeholder="••••••••"
                  prepend-inner-icon="ri-lock-line"
                  :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                  :rules="[requiredValidator]"
                  :error-messages="errors.password"
                  variant="outlined"
                  density="comfortable"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <VCol
                cols="12"
                class="pb-2"
              >
                <div class="d-flex align-center justify-space-between flex-wrap gap-2">
                  <VCheckbox
                    v-model="form.remember"
                    label="Ingat saya"
                    color="primary"
                    hide-details
                    density="compact"
                  />
                  <RouterLink
                    class="text-primary text-body-2"
                    :to="{ name: 'forgot-password' }"
                  >
                    Lupa Password?
                  </RouterLink>
                </div>
              </VCol>

              <VCol cols="12">
                <VBtn
                  type="submit"
                  block
                  color="primary"
                  size="large"
                  :loading="loading"
                  class="font-weight-bold"
                  style="height: 52px;"
                >
                  Masuk
                </VBtn>
              </VCol>
            </VRow>
          </VForm>

          <!-- Footer -->
          <div class="text-center mt-6">
            <p class="text-caption text-medium-emphasis mb-0">
              © {{ new Date().getFullYear() }} QUALIVA
            </p>
          </div>
        </div>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss" scoped>
.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  
  :deep(.v-img) {
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
    }
  }
}

// Responsive untuk mobile
@media (max-width: 960px) {
  .logo-container {
    min-height: 60px;
    
    :deep(.v-img) {
      max-width: 140px !important;
      max-height: 60px !important;
    }
  }
}

.auth-wrapper {
  min-height: 100vh;
}

// 👉 Hero Slide
.hero-slide {
  position: relative;
  width: 100%;
  height: 100vh;
}

// 👉 Overlay Gradient
.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(34, 111, 167, 0.25) 0%,
    rgba(0, 0, 0, 0.45) 100%
  );
  z-index: 1;
}

// ✅ KONTEN DI KIRI ATAS
.hero-content-left {
  position: absolute;
  top: 12%;
  left: 6%;
  z-index: 2;
  max-width: 520px;
  text-align: left;
}

.hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  line-height: 1.2;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
  line-height: 1.5;
}

// 👉 Floating Cards di KANAN (berjajar vertikal)
.floating-cards-right {
  position: absolute;
  top: 50%;
  right: 6%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 16px;
  pointer-events: none;
}

// 👉 Floating Card Style (Glassmorphism)
.floating-card {
  background: rgba(255, 255, 255, 0.88) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
  pointer-events: auto;
  animation: float 7s ease-in-out infinite;
  min-width: 200px;

  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: 2.3s; }
  &:nth-child(3) { animation-delay: 4.6s; }
}

// 👉 Animasi Float
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

// ✅ BOTTOM TEXT (TETAP ADA TERUS)
.bottom-text-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to right,
    rgba(34, 111, 167, 0.95),
    rgba(0, 0, 0, 0.85)
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 16px 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.bottom-text-content {
  display: flex;
  align-items: center;
  color: white;
  font-size: 0.95rem;
  font-weight: 500;
}

// 👉 Slider Indicators
.slider-indicators {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: auto;

  &.active {
    width: 32px;
    border-radius: 4px;
    background: white;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.7);
  }
}

// 👉 FIX: Cegah 2 gambar overlap
.hero-carousel {
  .v-carousel__item {
    position: absolute !important;
    inset: 0;
    opacity: 0;
    transition: opacity 0.6s ease-in-out;
    pointer-events: none;

    &.v-carousel__item--active {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .v-window__container {
    position: relative;
  }
}

// 👉 Responsive: Tablet
@media (max-width: 1280px) {
  .hero-title {
    font-size: 2.25rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .floating-card {
    min-width: 160px;
    max-width: 200px;
  }

  .floating-cards-right {
    right: 4%;
  }
}

// 👉 Responsive: Mobile
@media (max-width: 960px) {
  .auth-wrapper {
    flex-direction: column;
  }

  .hero-slide {
    height: 40vh;
    min-height: 300px;
  }

  .hero-content-left {
    top: 10%;
    left: 5%;
    max-width: 90%;
    text-align: center;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-subtitle {
    font-size: 0.875rem;
  }

  .floating-cards-right {
    display: none;
  }

  .bottom-text-container {
    padding: 12px 16px;
    
    .bottom-text-content {
      font-size: 0.85rem;
    }
  }

  .slider-indicators {
    bottom: 80px;
  }
}
</style>
